import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GripVertical, EyeOff, Maximize2, Minimize2 } from "lucide-react";
import { useDashboardConfigStore } from "@/store/dashboardConfigStore";
import { WidgetSize, WIDGET_REGISTRY, SIZE_LABEL_KEYS } from "@/config/dashboardWidgets";
import { cn } from "@/lib/utils";

interface WidgetWrapperProps {
  widgetId: string;
  children: ReactNode;
  className?: string;
}

export function WidgetWrapper({ widgetId, children, className }: WidgetWrapperProps) {
  const { t } = useTranslation("adminui2");
  const { isEditMode, toggleWidget, resizeWidget, config } = useDashboardConfigStore();
  
  const widgetConfig = config.widgets.find(w => w.id === widgetId);
  const widgetDef = WIDGET_REGISTRY[widgetId];
  const currentSize = widgetConfig?.size || widgetDef?.defaultSize || 'medium';

  if (!isEditMode) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={cn(
      "relative group",
      "ring-2 ring-dashed ring-primary/30 rounded-lg",
      "hover:ring-primary/50 transition-all",
      className
    )}>
      {/* Edit controls overlay */}
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 bg-background border rounded-full px-2 py-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Drag handle */}
        <div className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
        
        {/* Size selector */}
        <Select 
          value={currentSize} 
          onValueChange={(size: WidgetSize) => resizeWidget(widgetId, size)}
        >
          <SelectTrigger className="h-7 w-24 text-xs border-0 bg-transparent">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(SIZE_LABEL_KEYS).map(([size, labelKey]) => (
              <SelectItem key={size} value={size} className="text-xs">
                {t(labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Hide button */}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => toggleWidget(widgetId)}
        >
          <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </div>

      {/* Widget name badge */}
      <div className="absolute -top-2 -left-2 z-10 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full font-medium">
        {widgetDef ? t(widgetDef.nameKey) : widgetId}
      </div>

      {children}
    </div>
  );
}
